interface DragDropTouchType {
    DataTransfer: typeof DataTransfer
    getInstance: () => any
}

let DragDropTouch: DragDropTouchType | {} = {}
;(function (DragDropTouch_1: Record<string, any>) {
    /**
     * Object used to hold the data that is being dragged during drag and drop operations.
     *
     * It may hold one or more data items of different types. For more information about
     * drag and drop operations and data transfer objects, see
     * <a href="https://developer.mozilla.org/en-US/docs/Web/API/DataTransfer">HTML Drag and Drop API</a>.
     *
     * This object is created automatically by the @see:DragDropTouch singleton and is
     * accessible through the @see:dataTransfer property of all drag events.
     */
    class DataTransfer {
        private _dropEffect: string
        private _effectAllowed: string
        private _data: { [key: string]: string }

        constructor() {
            this._dropEffect = 'move'
            this._effectAllowed = 'all'
            this._data = {}
        }

        /**
         * Gets or sets the type of drag-and-drop operation currently selected.
         * The value must be 'none',  'copy',  'link', or 'move'.
         */
        get dropEffect(): string {
            return this._dropEffect
        }

        set dropEffect(value: string) {
            this._dropEffect = value
        }

        /**
         * Gets or sets the types of operations that are possible.
         * Must be one of 'none', 'copy', 'copyLink', 'copyMove', 'link',
         * 'linkMove', 'move', 'all' or 'uninitialized'.
         */
        get effectAllowed(): string {
            return this._effectAllowed
        }

        set effectAllowed(value: string) {
            this._effectAllowed = value
        }

        /**
         * Gets an array of strings giving the formats that were set in the @see:dragstart event.
         */
        get types(): string[] {
            return Object.keys(this._data)
        }

        /**
         * Removes the data associated with a given type.
         *
         * The type argument is optional. If the type is empty or not specified, the data
         * associated with all types is removed. If data for the specified type does not exist,
         * or the data transfer contains no data, this method will have no effect.
         *
         * @param type Type of data to remove.
         */
        clearData(type?: string): void {
            if (type != null) {
                delete this._data[type]
            } else {
                this._data = {}
            }
        }

        /**
         * Retrieves the data for a given type, or an empty string if data for that type does
         * not exist or the data transfer contains no data.
         *
         * @param type Type of data to retrieve.
         */
        getData(type: string): string {
            return this._data[type] || ''
        }

        /**
         * Set the data for a given type.
         *
         * For a list of recommended drag types, please see
         * https://developer.mozilla.org/en-US/docs/Web/Guide/HTML/Recommended_Drag_Types.
         *
         * @param type Type of data to add.
         * @param value Data to add.
         */
        setData(type: string, value: string): void {
            this._data[type] = value
        }

        /**
         * Set the image to be used for dragging if a custom one is desired.
         *
         * @param img An image element to use as the drag feedback image.
         * @param offsetX The horizontal offset within the image.
         * @param offsetY The vertical offset within the image.
         */
        setDragImage(img: HTMLElement, offsetX: number, offsetY: number): void {
            let ddt = (DragDropTouch as any)._instance as DragDropTouch
            ddt.setCustomDragImage(img)
            ddt.setImgOffset(offsetX, offsetY)
        }
    }

    DragDropTouch_1.DataTransfer = DataTransfer

    class DragDropTouch {
        private static _instance: DragDropTouch
        private static _THRESHOLD = 5
        private static _OPACITY = 0.5
        private static _DBLCLICK = 500
        private static _DRAGTIME = 500
        private static _rmvAtts = 'id,class,style,draggable'.split(',')
        private static _kbdProps = 'altKey,ctrlKey,metaKey,shiftKey'.split(',')
        private static _ptProps = 'pageX,pageY,clientX,clientY,screenX,screenY'.split(',')

        private _lastClick: number
        private _dragSource: HTMLElement | null = null
        private _img: HTMLElement | null = null
        private _imgCustom: HTMLElement | null = null
        private _imgOffset: { x: number; y: number } | null = null
        private _ptDown: { x: number; y: number } | null = null
        private _lastTouch: TouchEvent | null = null
        private _lastTarget: HTMLElement | null = null
        private _dataTransfer: DataTransfer
        private dragTimeout: any
        private dragEnabled: boolean = false

        public setCustomDragImage(img: HTMLElement): void {
            this._imgCustom = img
        }
        public setImgOffset(offsetX: number, offsetY: number): void {
            this._imgOffset = { x: offsetX, y: offsetY }
        }
        constructor() {
            this._lastClick = 0
            if (DragDropTouch._instance) {
                throw new Error('DragDropTouch instance already created.')
            }
            DragDropTouch._instance = this
            this._dataTransfer = new DataTransfer()

            let supportsPassive = false
            document.addEventListener('test', function () {}, {
                get passive() {
                    supportsPassive = true
                    return true
                },
            })

            if ('ontouchstart' in document) {
                const d = document
                const ts = this._touchstart.bind(this)
                const tm = this._touchmove.bind(this)
                const te = this._touchend.bind(this)
                const opt = supportsPassive ? { passive: false, capture: false } : false
                d.addEventListener('touchstart', ts, opt)
                d.addEventListener('touchmove', tm, opt)
                d.addEventListener('touchend', te)
                d.addEventListener('touchcancel', te)
            }
        }

        static getInstance(): DragDropTouch {
            return DragDropTouch._instance
        }

        private _shouldHandle(e: TouchEvent): boolean {
            return e && e.touches && e.touches.length < 2
        }

        private _reset(): void {
            this._dragSource = null
            this._img = null
            this._imgCustom = null
            this._ptDown = null
            this._lastTouch = null
            this._lastTarget = null
            this._imgOffset = null
        }

        private _getPoint(e: TouchEvent): { x: number; y: number } {
            let touch = e.changedTouches[0]
            return { x: touch.clientX, y: touch.clientY }
        }

        private _getDelta(e: TouchEvent): number {
            const p = this._getPoint(e)
            return Math.abs(p.x - this._ptDown!.x) + Math.abs(p.y - this._ptDown!.y)
        }

        private _getTarget(e: TouchEvent): HTMLElement {
            const pt = this._getPoint(e)
            return document.elementFromPoint(pt.x, pt.y) as HTMLElement
        }

        private _dispatchEvent(e: TouchEvent, type: string, target: HTMLElement): boolean {
            const evt = new Event(type, { bubbles: true, cancelable: true }) as any
            const t = e.touches ? e.touches[0] : (e as any)

            DragDropTouch._kbdProps.forEach((prop) => ((evt as any)[prop] = (e as any)[prop]))
            DragDropTouch._ptProps.forEach((prop) => (evt[prop] = t[prop]))

            evt.dataTransfer = this._dataTransfer

            return !target.dispatchEvent(evt)
        }

        private _createImage(e: TouchEvent): void {
            const src = this._imgCustom || (this._dragSource!.cloneNode(true) as HTMLElement)
            this._img = src

            DragDropTouch._rmvAtts.forEach((att) => this._img!.removeAttribute(att))

            this._copyStyle(this._dragSource!, this._img)

            const pt = this._getPoint(e)
            this._moveImage(e)

            document.body.appendChild(this._img)
            this._img.style.position = 'absolute'
            this._img.style.opacity = DragDropTouch._OPACITY.toString()
        }

        private _moveImage(e: TouchEvent): void {
            const pt = this._getPoint(e)
            const offsetX = this._imgOffset ? this._imgOffset.x : 0
            const offsetY = this._imgOffset ? this._imgOffset.y : 0
            this._img!.style.left = pt.x - offsetX + 'px'
            this._img!.style.top = pt.y - offsetY + 'px'
        }

        private _destroyImage(): void {
            if (this._img && this._img.parentElement) {
                this._img.parentElement.removeChild(this._img)
            }
            this._img = null
        }

        private _copyStyle(src: HTMLElement, dst: HTMLElement): void {
            ;(window.getComputedStyle(src) as any).forEach((style: string) => {
                dst.style.setProperty(style, src.style.getPropertyValue(style))
            })
            dst.style.pointerEvents = 'none'
        }

        private _touchstart(e: TouchEvent): void {
            if (!this._shouldHandle(e)) return
            const target = this._getTarget(e)
            if (target && target.draggable) {
                this._dragSource = target
                this._ptDown = this._getPoint(e)
                this._lastTouch = e
                this._lastClick = Date.now()
                this._dataTransfer = new DataTransfer()
                this.dragTimeout = setTimeout(() => {
                    this.dragEnabled = true
                    this._dispatchEvent(e, 'dragstart', this._dragSource!)
                }, DragDropTouch._DRAGTIME)
            }
        }

        private _touchmove(e: TouchEvent): void {
            if (!this._shouldHandle(e)) return
            if (!this._dragSource || !this.dragEnabled) return

            if (!this._img) this._createImage(e)
            this._moveImage(e)
        }

        private _touchend(e: TouchEvent): void {
            if (!this._shouldHandle(e)) return

            clearTimeout(this.dragTimeout)

            if (this._img) {
                this._destroyImage()

                if (this.dragEnabled) {
                    const target = this._getTarget(e)
                    this._dispatchEvent(e, 'dragend', this._dragSource!)
                    this._dispatchEvent(e, 'drop', target)
                }

                this._reset()
            }
        }
    }

    DragDropTouch_1.getInstance = DragDropTouch.getInstance
})(DragDropTouch as DragDropTouchType)

export { DragDropTouch }
